import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Factory, Shield, Award, CheckCircle, Truck, Users, Settings, ArrowRight, Star, Zap, Globe, Package, Clock, ThumbsUp, Target, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DataCableManufacturer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Set Page Title for SEO
    document.title = 'Best Data Cable Manufacturer in Noida | Premium Quality OEM ODM Factory | RSD Accessories';
    
    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'RSD Accessories Pvt Ltd is the best data cable manufacturer in Noida, India. Premium quality OEM ODM data cable factory with 3L+ cables/month production. Best data cable company offering Type-C cables, fast charging cables, USB data cables. Contact: 9857300030 / 9958559161';
    
    // Set Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = 'best data cable manufacturer in noida, data cable manufacturer noida, premium quality data cable, OEM ODM data cable factory, best data cable company india, Type-C cable manufacturer, USB cable manufacturer noida, fast charging cable manufacturer, data cable factory india';
    
    // Set Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://rsdaccessories.com/data-cable-manufacturer-in-noida';
    
    // Set Open Graph Tags
    const ogTags = {
      'og:title': 'Best Data Cable Manufacturer in Noida | RSD Accessories Pvt Ltd',
      'og:description': 'RSD Accessories is the best data cable manufacturer in Noida with 3L+ cables/month production. Premium quality OEM ODM solutions for Type-C, USB cables.',
      'og:type': 'website',
      'og:url': 'https://rsdaccessories.com/data-cable-manufacturer-in-noida',
      'og:image': '/images/site/site_7.jpg',
      'og:site_name': 'RSD Accessories',
      'og:locale': 'en_IN'
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Set Twitter Card Tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Best Data Cable Manufacturer in Noida | RSD Accessories',
      'twitter:description': 'Premium quality OEM ODM data cable factory with 3L+ cables/month. Best data cable company in Noida, India.',
      'twitter:image': '/images/site/site_7.jpg'
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    
    // Add Schema Markup for Local Business
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'local-business-schema';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "RSD Accessories Pvt Ltd - Best Data Cable Manufacturer in Noida",
      "description": "RSD Accessories Pvt Ltd is the best data cable manufacturer in Noida, India. Premium quality OEM ODM data cable factory with 3L+ cables/month production capacity.",
      "url": "https://rsdaccessories.com/data-cable-manufacturer-in-noida",
      "logo": "https://rsdaccessories.com/logo.png",
      "image": "/images/site/site_7.jpg",
      "telephone": ["+91-9857300030", "+91-9958559161"],
      "email": "contact@rsdaccessories.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-08, Sector 57",
        "addressLocality": "Noida",
        "addressRegion": "Uttar Pradesh",
        "postalCode": "201301",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "28.6081",
        "longitude": "77.3649"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "19:00"
      },
      "priceRange": "₹₹",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "500"
      },
      "sameAs": [
        "https://www.facebook.com/rsdaccessories",
        "https://www.instagram.com/rsdaccessories",
        "https://www.linkedin.com/company/rsdaccessories"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "India"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Data Cables",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Type-C to Type-C 100W PD Cable",
              "category": "Data Cables"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "USB-A to Type-C Fast Charging Cable",
              "category": "Data Cables"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Braided Nylon Data Cable",
              "category": "Data Cables"
            }
          }
        ]
      }
    });
    
    // Remove existing schema if present
    const existingSchema = document.getElementById('local-business-schema');
    if (existingSchema) {
      existingSchema.remove();
    }
    document.head.appendChild(schemaScript);
    
    // Cleanup on unmount
    return () => {
      const schema = document.getElementById('local-business-schema');
      if (schema) {
        schema.remove();
      }
    };
  }, []);

  const products = [
    {
      name: 'Premium Braided Nylon Cable',
      description: 'Best quality braided data cables with 40,000+ bend lifespan and reinforced stress points for maximum durability',
      image: '/images/site/site_11.jpg'
    },
    {
      name: 'RSD Branded White Cable',
      description: 'Premium quality Type-C data cables with RSD branding - perfect for OEM and ODM partnerships',
      image: '/images/site/site_6.jpg'
    },
    {
      name: '45W Fast Charging Cable',
      description: 'Best data cable factory in India producing premium 45W fast charging cables with Type-C and Micro USB options',
      image: '/images/site/site_7.jpg'
    },
    {
      name: 'Premium Silicone Cable',
      description: 'Ultra-soft silicone coating with gold-plated connectors - best quality data cables for premium brands',
      image: '/images/site/site_2.jpg'
    },
    {
      name: 'Type-C to Type-C Cable',
      description: 'Premium quality 100W PD fast charging cables - best data cable company for OEM ODM manufacturing',
      image: '/images/site/site_4.jpg'
    },
    {
      name: 'USB-A to Type-C Cables',
      description: 'Gold-plated connectors with premium quality fast charging support - manufactured at our Noida factory',
      image: '/images/site/site_45.png'
    }
  ];

  const whyChooseUs = [
    { icon: Factory, title: 'Best Data Cable Factory in Noida', description: 'State-of-the-art manufacturing unit in Sector 57, Noida with modern German machinery and automated production lines' },
    { icon: Settings, title: 'OEM & ODM Manufacturing', description: 'Complete OEM ODM solutions for brands worldwide - custom private label manufacturing with your branding' },
    { icon: Shield, title: 'Premium Quality Assurance', description: 'ISO certified quality control with rigorous 12-point testing for every cable - best quality guaranteed' },
    { icon: Truck, title: '3L+ Cables/Month Capacity', description: 'Large scale premium production capability - best data cable company for bulk and wholesale orders' },
    { icon: Award, title: 'Best Quality Standards', description: 'Every cable tested for durability, conductivity, and safety - premium quality at competitive prices' },
    { icon: Users, title: 'Trusted by 500+ Brands', description: 'Best data cable manufacturer serving distributors and retailers across India and 15+ countries globally' }
  ];

  const cableTypes = [
    { name: 'Type-C to Type-C', power: '100W PD', icon: Zap },
    { name: 'USB-A to Type-C', power: '65W QC', icon: Zap },
    { name: 'USB-A to Micro', power: '18W', icon: Zap },
    { name: 'Lightning Cables', power: '20W PD', icon: Zap },
    { name: 'Multi-Pin Cables', power: '3-in-1', icon: Layers },
    { name: 'Magnetic Cables', power: 'Universal', icon: Target }
  ];

  const certifications = [
    'ISO 9001:2015 Certified',
    'BIS Certified Products',
    'RoHS Compliant',
    'CE Certified',
    'FCC Approved',
    'Quality Tested'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Breadcrumb for SEO */}
              <nav className="mb-4 text-sm" aria-label="Breadcrumb">
                <ol className="flex items-center gap-2 text-neutral-400">
                  <li><Link to="/" className="hover:text-[#FF4D00] transition-colors">Home</Link></li>
                  <li>/</li>
                  <li className="text-[#FF4D00]">Data Cable Manufacturer</li>
                </ol>
              </nav>
              <div className="inline-flex items-center gap-2 bg-[#FF4D00]/20 border border-[#FF4D00]/30 rounded-full px-4 py-2 mb-6">
                <Factory className="w-4 h-4 text-[#FF4D00]" />
                <span className="text-sm text-[#FF4D00] font-medium">Best Data Cable Factory in India | Made in Noida</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
                Best <span className="text-[#FF4D00]">Data Cable Manufacturer</span> in Noida
              </h1>
              <p className="text-lg md:text-xl text-neutral-300 mb-4 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> is the <strong>best data cable manufacturer in Noida</strong>, India. We are a leading <strong>premium quality OEM ODM data cable factory</strong> with our own brand <strong>RSD - A Brand of New Eon</strong>.
              </p>
              <p className="text-base text-neutral-400 mb-8 leading-relaxed">
                As the <strong>best data cable company in India</strong>, we specialize in manufacturing <strong>premium quality</strong> Type-C cables, fast charging cables, USB data cables, and mobile accessories. Our state-of-the-art <strong>data cable factory in Noida</strong> produces <strong>3 Lakh+ cables every month</strong> with uncompromising quality standards.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:+919857300030">
                  <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9857300030
                  </Button>
                </a>
                <a href="tel:+919958559161">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    Call: 9958559161
                  </Button>
                </a>
              </div>
              <div className="mt-6">
                <Link to="/become-distributor">
                  <Button variant="link" className="text-[#FF4D00] hover:text-white px-0 text-lg">
                    Become a Distributor
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/site/site_7.jpg"
                alt="Best Data Cable Manufacturer in Noida - RSD Accessories Premium Quality OEM ODM Factory"
                className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#FF4D00] text-white px-6 py-3 rounded-xl font-bold shadow-lg">
                Best Quality OEM ODM
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-[#FF4D00] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <p className="text-4xl md:text-5xl font-black">3L+</p>
              <p className="text-sm md:text-base opacity-90">Cables/Month</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">500+</p>
              <p className="text-sm md:text-base opacity-90">Brand Partners</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">50+</p>
              <p className="text-sm md:text-base opacity-90">Product Variants</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black">15+</p>
              <p className="text-sm md:text-base opacity-90">Countries Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-neutral-50" id="about">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-900">
                About <span className="text-[#FF4D00]">RSD Accessories</span> - Best Data Cable Company in India
              </h2>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                <strong>RSD Accessories Pvt Ltd</strong> is recognized as the <strong>best data cable manufacturer in Noida</strong> and one of the top <strong>premium quality data cable factories in India</strong>. Our manufacturing facility in Sector 57, Noida is equipped with cutting-edge German technology and automated production lines that ensure consistent <strong>best quality</strong> output.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                As a leading <strong>OEM ODM data cable company</strong>, we serve some of the biggest brands in India and internationally. Our <strong>premium quality</strong> cables are known for their durability, fast charging capabilities, and superior build quality. We proudly own the brand <strong>RSD - A Brand of New Eon</strong>, which represents our commitment to innovation and excellence.
              </p>
              <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                Being the <strong>best data cable factory in India</strong>, we understand the importance of quality and reliability. Every cable that leaves our factory undergoes rigorous 12-point quality testing to ensure it meets international standards. Our <strong>premium quality OEM ODM</strong> solutions have helped hundreds of brands establish their presence in the market.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">3L+</p>
                  <p className="text-neutral-600">Cables/Month</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">500+</p>
                  <p className="text-neutral-600">Brand Partners</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">50+</p>
                  <p className="text-neutral-600">Product Variants</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border">
                  <p className="text-3xl font-black text-[#FF4D00]">100%</p>
                  <p className="text-neutral-600">Quality Tested</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="/images/site/site_6.jpg"
                alt="RSD Accessories - Best Quality Premium Data Cable Manufacturer in Noida India"
                className="w-full rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Cable Types Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Premium Quality <span className="text-[#FF4D00]">Cable Types</span> We Manufacture
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best data cable manufacturer in Noida</strong>, we offer a complete range of <strong>premium quality</strong> cables for every need - from fast charging to data transfer solutions.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {cableTypes.map((cable, index) => (
              <div key={index} className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl p-6 text-center text-white hover:scale-105 transition-transform">
                <cable.icon className="w-10 h-10 mx-auto mb-3 text-[#FF4D00]" />
                <h3 className="font-bold text-sm mb-1">{cable.name}</h3>
                <p className="text-xs text-neutral-400">{cable.power}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-neutral-50" id="products">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Quality Product Range</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best data cable factory in India</strong>, we manufacture a wide range of <strong>premium quality</strong> cables including Type-C cables, fast charging cables, USB data cables, and custom <strong>OEM ODM</strong> solutions. Our <strong>best quality</strong> products are trusted by leading brands across the country.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <div key={index} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-neutral-100">
                <div className="aspect-square overflow-hidden bg-neutral-100">
                  <img 
                    src={product.image}
                    alt={`${product.name} - Best Data Cable Manufacturer Noida Premium Quality`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{product.name}</h3>
                  <p className="text-neutral-600">{product.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop?category=data-cables">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                View All Products
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white" id="why-us">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why RSD is the <span className="text-[#FF4D00]">Best Data Cable Company</span> in India?
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              As the <strong>best data cable manufacturer in Noida</strong>, we deliver <strong>premium quality</strong> products with excellence at every step. Our <strong>OEM ODM</strong> services are trusted by 500+ brands worldwide.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#FF4D00]/50 transition-all duration-300">
                <div className="w-14 h-14 bg-[#FF4D00] rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-neutral-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Quality <span className="text-[#FF4D00]">Certifications</span> & Standards
            </h2>
            <p className="text-lg text-neutral-600">
              Our <strong>premium quality</strong> products meet international standards - that's why we're the <strong>best data cable factory in India</strong>
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-neutral-100 rounded-full px-6 py-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#FF4D00]" />
                <span className="font-medium text-neutral-700">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-neutral-50" id="gallery">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Products Gallery</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              <strong>Best quality data cables</strong> manufactured at our <strong>premium data cable factory in Noida</strong> with strict quality control. As the <strong>best data cable company in India</strong>, we take pride in every product we manufacture.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {products.map((product, index) => (
              <div key={index} className="aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <img 
                  src={product.image}
                  alt={`RSD Premium Quality Data Cable ${index + 1} - Best Manufacturer Noida India`}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OEM ODM Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#FF4D00] to-orange-500 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Premium Quality OEM & ODM Manufacturing Services
                </h2>
                <p className="text-lg mb-6 text-white/90">
                  Looking for the <strong>best data cable manufacturer in Noida</strong> for your brand? RSD Accessories is the <strong>best data cable company</strong> offering complete <strong>OEM ODM</strong> and private label solutions with <strong>premium quality</strong> assurance:
                </p>
                <ul className="space-y-3">
                  {[
                    'Custom branding & premium packaging design',
                    'Minimum order quantities from 1000 pcs',
                    'Multiple cable types, lengths & colors',
                    'Best quality certification included',
                    'Pan-India & international delivery',
                    'Dedicated account manager for OEM ODM orders',
                    'Free sampling for bulk orders',
                    'Best pricing for premium quality cables'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <img 
                  src="/images/site/site_2.jpg"
                  alt="Premium Quality OEM ODM Data Cable Manufacturing Noida - Best Factory"
                  className="w-full max-w-sm mx-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Process Section */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900">
              Our <span className="text-[#FF4D00]">Premium Manufacturing Process</span>
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              As the <strong>best data cable factory in India</strong>, we follow a rigorous manufacturing process to ensure every cable meets our <strong>premium quality</strong> standards. This is what makes us the <strong>best data cable manufacturer in Noida</strong>.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Raw Material Selection', desc: 'We source only the best quality copper, premium PVC, and gold-plated connectors for our cables' },
              { step: '02', title: 'Precision Manufacturing', desc: 'German automated machinery ensures consistent premium quality production at our Noida factory' },
              { step: '03', title: '12-Point Quality Testing', desc: 'Every cable undergoes rigorous testing for conductivity, durability, and safety standards' },
              { step: '04', title: 'Premium Packaging', desc: 'Best quality packaging with custom branding options for OEM ODM partners' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100">
                <div className="text-5xl font-black text-[#FF4D00]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{item.title}</h3>
                <p className="text-neutral-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Benefits of Partnering with the <span className="text-[#FF4D00]">Best Data Cable Company</span>
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              Join 500+ brands who trust RSD Accessories as their <strong>premium quality data cable manufacturer</strong>. Experience the <strong>best OEM ODM</strong> services in India.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Package, title: 'Bulk Discounts', desc: 'Best pricing for premium quality cables on large orders' },
              { icon: Clock, title: 'Fast Delivery', desc: '3L+ cables/month capacity ensures timely delivery' },
              { icon: ThumbsUp, title: 'Quality Guarantee', desc: '100% tested premium quality with replacement warranty' },
              { icon: Globe, title: 'Pan-India Shipping', desc: 'Free shipping on bulk orders across India' }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                <item.icon className="w-12 h-12 mx-auto mb-4 text-[#FF4D00]" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-neutral-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-neutral-900 text-white" id="contact">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Contact the <span className="text-[#FF4D00]">Best Data Cable Manufacturer</span>
            </h2>
            <p className="text-lg text-neutral-300 max-w-3xl mx-auto">
              Get in touch with RSD Accessories Pvt Ltd - Your trusted <strong>premium quality data cable manufacturer in Noida</strong>. We are the <strong>best data cable company</strong> for all your <strong>OEM ODM</strong> needs.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <a href="tel:+919857300030" className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/10 transition-all border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Call Us</h3>
              <p className="text-[#FF4D00] text-lg font-semibold">9857300030</p>
              <p className="text-[#FF4D00] text-lg font-semibold">9958559161</p>
              <p className="text-neutral-400 mt-2">Mon-Sat: 9AM - 7PM</p>
            </a>
            
            <a href="mailto:contact@rsdaccessories.com" className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/10 transition-all border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Email Us</h3>
              <p className="text-[#FF4D00] text-lg font-semibold">contact@rsdaccessories.com</p>
              <p className="text-neutral-400 mt-2">We reply within 24 hours</p>
            </a>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10">
              <div className="w-16 h-16 bg-[#FF4D00] rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Factory Address</h3>
              <p className="text-neutral-300">RSD Accessories Pvt Ltd</p>
              <p className="text-neutral-400 mt-2">B-08, Sector 57,<br />Noida 201301,<br />Uttar Pradesh, India</p>
            </div>
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/become-distributor">
              <Button className="bg-[#FF4D00] hover:bg-[#e64500] text-white px-8 py-6 text-lg rounded-full">
                Become a Distributor
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/919857300030" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-neutral-900 px-8 py-6 text-lg rounded-full">
                WhatsApp Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Best Data Cable Manufacturer in Noida - RSD Accessories Pvt Ltd</h2>
            <p className="text-neutral-600 mb-4">
              <strong>RSD Accessories Pvt Ltd</strong> is proudly recognized as the <strong>best data cable manufacturer in Noida</strong>, Uttar Pradesh, India. Our state-of-the-art <strong>data cable factory</strong> is equipped with German precision machinery and automated production lines that enable us to produce over <strong>3 Lakh premium quality cables every month</strong>. As the <strong>best data cable company in India</strong>, we are committed to delivering excellence in every product we manufacture.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Premium Quality OEM ODM Data Cable Manufacturing</h3>
            <p className="text-neutral-600 mb-4">
              As a leading <strong>OEM ODM data cable manufacturer</strong>, we specialize in custom manufacturing solutions for brands across India and internationally. Our <strong>premium quality</strong> cables are manufactured using the finest raw materials including high-grade copper conductors, gold-plated connectors, and durable PVC/TPE/Nylon braiding. Being the <strong>best data cable factory in India</strong>, we ensure that every cable undergoes our rigorous 12-point quality testing process before shipping.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Why We Are the Best Data Cable Company in India</h3>
            <p className="text-neutral-600 mb-4">
              What makes RSD Accessories the <strong>best data cable manufacturer in Noida</strong> is our unwavering commitment to <strong>premium quality</strong> and customer satisfaction. Our <strong>data cable factory</strong> houses advanced machinery from Germany and Japan, enabling us to maintain consistent quality across millions of units. We serve as the trusted <strong>OEM ODM</strong> partner for over 500 brands, ranging from startups to multinational corporations.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Complete Range of Premium Quality Data Cables</h3>
            <p className="text-neutral-600 mb-4">
              Being the <strong>best data cable factory in India</strong>, we manufacture a comprehensive range of cables including Type-C to Type-C 100W PD cables, USB-A to Type-C fast charging cables, USB-A to Micro USB cables, Lightning cables, 3-in-1 multi-pin cables, magnetic cables, braided nylon cables, and premium silicone cables. Our <strong>best quality</strong> products support all major fast charging protocols including USB Power Delivery, Qualcomm Quick Charge, Samsung Adaptive Fast Charging, and OnePlus Warp Charge.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Best Quality Standards & Certifications</h3>
            <p className="text-neutral-600 mb-4">
              Our <strong>premium quality data cable factory in Noida</strong> is ISO 9001:2015 certified, and all our products are BIS, RoHS, CE, and FCC compliant. This commitment to international standards is what makes us the <strong>best data cable manufacturer in Noida</strong> and the preferred choice for <strong>OEM ODM</strong> partnerships. Every <strong>premium quality</strong> cable we produce is tested for electrical conductivity, tensile strength, bend resistance, connector durability, and safety compliance.
            </p>
            
            <h3 className="text-xl font-bold text-neutral-900 mb-3">Contact the Best Data Cable Manufacturer Today</h3>
            <p className="text-neutral-600">
              Partner with <strong>RSD Accessories Pvt Ltd</strong> - the <strong>best data cable company in India</strong> for all your <strong>premium quality OEM ODM</strong> requirements. Visit our factory at <strong>B-08, Sector 57, Noida 201301</strong> or call us at <strong>9857300030 / 9958559161</strong>. As the <strong>best data cable manufacturer in Noida</strong>, we are ready to serve your business with <strong>premium quality</strong> products and exceptional service. Experience the difference of working with the <strong>best data cable factory in India</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Internal Links Section for SEO */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
            Explore More from <span className="text-[#FF4D00]">RSD Accessories</span>
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Link to Homepage */}
            <Link to="/" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">RSD Accessories Home</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Visit our homepage to explore all products and services</p>
            </Link>
            
            {/* Link to Shop */}
            <Link to="/shop" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Shop Data Cables</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Browse our complete range of premium quality data cables</p>
            </Link>
            
            {/* Link to Mobile Charger Page */}
            <Link to="/best-mobile-charger-manufacturer-in-noida" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Mobile Charger Manufacturer in Noida</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best mobile charger manufacturer - 15W to 120W fast chargers OEM</p>
            </Link>
            
            {/* Link to OEM Manufacturer Page */}
            <Link to="/oem-mobile-accessories-manufacturer-in-india" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">OEM Mobile Accessories Manufacturer India</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Best OEM private label mobile accessories factory in India</p>
            </Link>
            
            {/* Link to Become Distributor */}
            <Link to="/become-distributor" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Become a Distributor</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Partner with the best data cable company in India</p>
            </Link>
            
            {/* Link to About Us */}
            <Link to="/about" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">About RSD Accessories</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Learn about our journey as India's best cable manufacturer</p>
            </Link>
            
            {/* Link to Contact */}
            <Link to="/contact" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Contact Us</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Get in touch for OEM ODM inquiries and bulk orders</p>
            </Link>
            
            {/* Link to Data Cables Category */}
            <Link to="/shop?category=data-cables" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Type-C Cables</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Premium 100W PD fast charging Type-C cables</p>
            </Link>
            
            {/* Link to Warranty */}
            <Link to="/warranty-registration" className="group bg-neutral-50 rounded-2xl p-6 hover:bg-[#FF4D00] transition-all duration-300 border border-neutral-100">
              <h3 className="text-lg font-bold text-neutral-900 group-hover:text-white mb-2">Warranty Registration</h3>
              <p className="text-neutral-600 group-hover:text-white/80 text-sm">Register your RSD products for 6-month warranty</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#FF4D00]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Partner with the Best Data Cable Manufacturer?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Get premium quality OEM ODM data cables from the best data cable factory in Noida, India
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919857300030">
              <Button className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9857300030
              </Button>
            </a>
            <a href="tel:+919958559161">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg rounded-full font-bold">
                <Phone className="w-5 h-5 mr-2" />
                Call: 9958559161
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataCableManufacturer;
